import { GlobalShoppingContext } from '@psl/flows/shop/context';
import { memo, ReactNode, useCallback, useMemo, useState } from 'react';
import { BehaviorSubject } from 'rxjs';
import { useGlobalSearchContext } from '../../../appComponents/search/GlobalSearchContext';
import { useIsMobileLayout } from '../../../components/styles/responsive';
import useBehaviorSubject from '../../../functions/hooks/useBehaviorSubject';
import { useCreation } from '../../../functions/hooks/useCreation';
import { usePersistFn } from '../../../functions/hooks/usePersistFn';
import { RichFeaturedTagItem } from '../../../generated/api';
import { SortInfo } from '../functions/productSearchSortOptions';
import { useFeaturedTags } from '../functions/useFeaturedTags';
import { SearchSettingsItem } from '../functions/useSearchProducts';

const GlobalShoppingDefaultProvider = memo(
  ({ children }: { children?: ReactNode }) => {
    const globalSearchContext = useGlobalSearchContext();
    const useGlobalSearch = !useIsMobileLayout();
    const defaultQuery$ = useCreation(
      () => new BehaviorSubject<string>(''),
      [],
    );
    const query$ = useGlobalSearch ? globalSearchContext.query$ : defaultQuery$;
    const featuredTags = useFeaturedTags();

    const [selectedFeaturedTags, setSelectedFeaturedTags] = useState<
      RichFeaturedTagItem[]
    >([]);

    const [sortInfo, setSortInfo] = useState<SortInfo>(null);

    const [query, updateQuery] = useBehaviorSubject(query$);

    const searchSettingItems = useMemo<SearchSettingsItem[]>(() => {
      const res: SearchSettingsItem[] = [];
      if (sortInfo != null) {
        res.push({
          type: 'sort',
          sortBy: sortInfo.sortBy,
          sortOrder: sortInfo.sortOrder,
        });
      }
      const selectedFeaturedTagSet = new Set(
        selectedFeaturedTags.map(x => x.id),
      );
      const tagList = [
        ...selectedFeaturedTags,
        ...featuredTags.filter(x => !selectedFeaturedTagSet.has(x.id)),
      ];
      for (const featuredTag of tagList) {
        res.push({
          type: 'featuredTag',
          tagItem: featuredTag,
          selected: selectedFeaturedTagSet.has(featuredTag.id),
        });
      }
      return res;
    }, [featuredTags, selectedFeaturedTags, sortInfo]);

    const addFeaturedTag = useCallback((item: RichFeaturedTagItem) => {
      setSelectedFeaturedTags(list =>
        list.some(x => x.id === item.id) ? list : [...list, item],
      );
    }, []);

    const removeFeaturedTag = useCallback((item: RichFeaturedTagItem) => {
      setSelectedFeaturedTags(list => list.filter(x => x.id !== item.id));
    }, []);

    const onSearchSettingsItemPress = usePersistFn(
      (item: SearchSettingsItem) => {
        if (item.type === 'sort') {
          setSortInfo(null);
        } else {
          if (selectedFeaturedTags.some(x => x.id === item.tagItem.id)) {
            removeFeaturedTag(item.tagItem);
          } else {
            addFeaturedTag(item.tagItem);
          }
        }
      },
    );

    return (
      <GlobalShoppingContext.Provider
        value={{
          query$,
          query,
          updateQuery,
          searchSettingItems,
          featuredTags,
          addFeaturedTag,
          removeFeaturedTag,
          selectedFeaturedTags,
          setSelectedFeaturedTags,
          sortInfo,
          setSortInfo,
          onSearchSettingsItemPress,
        }}
      >
        {children}
      </GlobalShoppingContext.Provider>
    );
  },
);

export default GlobalShoppingDefaultProvider;
